.page {
  max-width: 900px;
  margin: 0 auto;
  padding: 32px 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 28px;
  animation: fade-up 0.55s var(--kh-ease-out) both;
}

/* ─── Top nav ─── */

.topNav {
  display: flex;
  align-items: center;
  gap: 10px;
}

.backBtn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.18);
  border: 0.5px solid rgba(255, 255, 255, 0.4);
  border-radius: 20px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition:
    background var(--kh-duration-fast),
    border-color var(--kh-duration-fast);
}

.backBtn:hover {
  background: rgba(255, 255, 255, 0.28);
  border-color: rgba(255, 255, 255, 0.6);
}

/* ─── New key reveal ─── */

.keyReveal {
  background: var(--kh-surface);
  border: 1px solid var(--kh-green, #22c55e);
  border-radius: var(--kh-radius-card);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.keyRevealHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.keyRevealTitle {
  font-size: var(--kh-size-body);
  font-weight: 700;
  color: var(--kh-text-primary);
}

.keyRevealClose {
  background: none;
  border: none;
  color: var(--kh-text-muted);
  cursor: pointer;
  font-size: var(--kh-size-meta);
}

.keyRevealClose:hover {
  color: var(--kh-text-primary);
}

.keyRevealWarning {
  margin: 0;
  font-size: var(--kh-size-meta);
  color: var(--kh-text-secondary);
}

.keyRevealValue {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--kh-surface-2);
  border: 0.5px solid var(--kh-border);
  border-radius: var(--kh-radius-sm);
  padding: 10px 14px;
  overflow: hidden;
}

.keyCode {
  flex: 1;
  font-family: var(--kh-font-mono);
  font-size: 13px;
  color: var(--kh-text-primary);
  word-break: break-all;
}

.copyBtn {
  background: rgba(255, 255, 255, 0.12);
  border: 0.5px solid var(--kh-border-hover);
  border-radius: var(--kh-radius-sm);
  color: var(--kh-text-primary);
  padding: 6px 14px;
  font-size: var(--kh-size-meta);
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background var(--kh-duration-fast),
    border-color var(--kh-duration-fast);
}

.copyBtn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
}

.keyRevealConfig {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.configLabel {
  font-size: var(--kh-size-meta);
  color: var(--kh-text-muted);
  font-weight: 600;
}

.configBlock {
  margin: 0;
  font-family: var(--kh-font-mono);
  font-size: 12px;
  color: var(--kh-text-secondary);
  background: var(--kh-surface-2);
  border: 0.5px solid var(--kh-border);
  border-radius: var(--kh-radius-sm);
  padding: 12px 14px;
  overflow-x: auto;
  white-space: pre;
}

/* ─── Section ─── */

.section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sectionHeader {
  display: flex;
  align-items: center;
  gap: 10px;
}

.sectionHeader::after {
  content: "";
  flex: 1;
  height: 0.5px;
  background: rgba(255, 255, 255, 0.2);
}

.sectionLabel {
  font-family: var(--kh-font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: #ffffff;
  text-transform: uppercase;
  flex-shrink: 0;
}

.versionBadge {
  font-family: var(--kh-font-mono);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--kh-text-secondary);
  padding: 2px 8px;
  border: 0.5px solid var(--kh-border);
  border-radius: 999px;
  flex-shrink: 0;
}

.installOptions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
}

.installOption {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  border-right: 0.5px solid var(--kh-border);
}

.installOption:last-child {
  border-right: none;
}

@media (max-width: 720px) {
  .installOptions {
    grid-template-columns: 1fr;
  }
  .installOption {
    border-right: none;
    border-bottom: 0.5px solid var(--kh-border);
  }
  .installOption:last-child {
    border-bottom: none;
  }
}

.installOptionHeader {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.installOptionLabel {
  font-family: var(--kh-font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: var(--kh-text-muted);
  text-transform: uppercase;
}

.installOptionTitle {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
}

.installOptionMeta {
  font-size: var(--kh-size-meta);
  color: var(--kh-text-secondary);
  line-height: 1.4;
}

.installOptionMeta code,
.installOption code {
  font-family: var(--kh-font-mono);
  font-size: 11px;
  padding: 1px 5px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  word-break: break-word;
}

.installSteps {
  margin: 0;
  padding: 0 0 0 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: var(--kh-size-meta);
  color: var(--kh-text-secondary);
  line-height: 1.5;
}

/* ─── Card ─── */

.card {
  background: var(--kh-surface);
  border: 0.5px solid var(--kh-border);
  border-radius: var(--kh-radius-card);
  box-shadow: var(--kh-shadow-1);
  overflow: hidden;
}

.cardTop {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 0.5px solid var(--kh-border);
}

.cardDesc {
  margin: 0;
  font-size: var(--kh-size-meta);
  color: var(--kh-text-secondary);
  line-height: 1.5;
}

.generateBtn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: var(--kh-red, #cb163a);
  border: none;
  border-radius: 20px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background var(--kh-duration-fast);
}

.generateBtn:hover {
  background: var(--kh-red-hover, #e01a42);
}

.empty {
  padding: 28px 20px;
  color: var(--kh-text-muted);
  font-size: var(--kh-size-body);
  text-align: center;
  margin: 0;
}

/* ─── Key list ─── */

.list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 20px;
  border-bottom: 0.5px solid var(--kh-border);
  transition: background var(--kh-duration-fast);
}

.row:last-child {
  border-bottom: none;
}

.row:hover {
  background: var(--kh-surface-2);
}

.rowRevoked {
  opacity: 0.5;
}

.rowMain {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.keyName {
  font-size: var(--kh-size-body);
  font-weight: 600;
  color: var(--kh-text-primary);
}

.keyPrefix {
  font-family: var(--kh-font-mono);
  font-size: var(--kh-size-meta);
  color: var(--kh-text-muted);
}

.rowMeta {
  display: flex;
  gap: 20px;
  flex-shrink: 0;
}

.metaItem {
  font-size: var(--kh-size-meta);
  color: var(--kh-text-secondary);
  white-space: nowrap;
}

.revokeBtn {
  background: none;
  border: 0.5px solid var(--kh-border-hover);
  color: var(--kh-text-muted);
  border-radius: var(--kh-radius-sm);
  padding: 4px 10px;
  font-size: var(--kh-size-meta);
  cursor: pointer;
  flex-shrink: 0;
  transition:
    color var(--kh-duration-fast),
    border-color var(--kh-duration-fast);
}

.revokeBtn:hover {
  color: var(--kh-red);
  border-color: var(--kh-red);
}

/* ─── Revoked section ─── */

.revokedSection {
  border-top: 0.5px solid var(--kh-border);
  padding-top: 8px;
}

.revokedLabel {
  display: block;
  padding: 8px 20px 0;
  font-family: var(--kh-font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--kh-text-muted);
  text-transform: uppercase;
}

.revokedBadge {
  font-size: var(--kh-size-meta);
  color: var(--kh-text-muted);
  font-style: italic;
}

@keyframes fade-up {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
